/*
 * ApprovalQueue.tsx — S66 + S-UI-P0: Approval Queue
 * Tab 1: Pending Approvals (GET /api/v1/exceptions/pending-approval)
 * Tab 2: Workflow Config (GET /api/v1/workflows, GET /api/v1/workflows/stats)
 */
import React, { useState, useEffect } from 'react';
import useTabState from '../hooks/useTabState';
import { API_V1, authFetch, formatAmount, safeFetch, titleCase } from '../constants';
import { useApp } from '../contexts/AppContext';
import { realtimeService } from '../services/realtimeService';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';

// Compute DOA level from amount — reads live config, falls back to defaults
let _cachedDOA: Record<string, number> | null = null;
function _loadDOA() {
  if (_cachedDOA) return;
  authFetch(`${API_V1}/org/configuration/doa-levels`)
    .then(r => r.ok ? r.json() : null)
    .then(data => { if (data) _cachedDOA = data; })
    .catch(() => {});
}

function getRequiredLevel(amount: number): string {
  _loadDOA();
  const abs = Math.abs(amount || 0);
  const l2 = _cachedDOA?.L2 ?? 50000;
  const l3 = _cachedDOA?.L3 ?? 250000;
  const l4 = _cachedDOA?.L4 ?? 1000000;
  const l5 = _cachedDOA?.L5 ?? 5000000;
  const l6 = _cachedDOA?.L6 ?? 50000000;
  if (abs <= l2) return 'L2';
  if (abs <= l3) return 'L3';
  if (abs <= l4) return 'L4';
  if (abs <= l5) return 'L5';
  if (abs <= l6) return 'L6';
  return 'L7';
}

const LEVEL_COLORS: Record<string, string> = {
  L2: '#64748B', L3: '#F59E0B', L4: '#FB923C', L5: 'var(--navy-800)', L6: 'var(--navy-800)', L7: '#A78BFA',
};

interface PendingApproval {
  id: number;
  exception_ref: string;
  exception_type: string;
  severity: string;
  status: string;
  amount: number;
  department: string;
  escalation_level: string;
  sla_deadline: string | null;
  created_at: string;
  can_approve: boolean;
  permissions?: Record<string, any>;
  ai_recommendation?: string;
  ai_confidence?: number | null;
}

interface WorkflowTemplate {
  id: number;
  code: string;
  name: string;
  priority: number;
  match_criteria: Record<string, any>;
  steps: any[];
  is_active: boolean;
}

interface WorkflowStats {
  templates_total: number;
  templates_active: number;
  instances_total: number;
  instances_pending: number;
  instances_approved: number;
  instances_rejected: number;
  sla_breached: number;
}

const AQ_TABS = ['pending', 'config'] as const;
type Tab = typeof AQ_TABS[number];

export default function ApprovalQueue() {
  const { role, authUser } = useApp();
  const [tab, setTab] = useTabState<Tab>('tab', AQ_TABS, 'pending');
  const [pending, setPending] = useState<PendingApproval[]>([]);
  const [needsEscalation, setNeedsEscalation] = useState<PendingApproval[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [stats, setStats] = useState<WorkflowStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPending = () => {
    const roleDept = Array.isArray(role.departments) ? role.departments[0] || 'all' : 'all';
    const params = `org_id=${authUser?.organization_id || 1}&agent_level=${role.level}&agent_department=${encodeURIComponent(roleDept)}`;
    return safeFetch(`${API_V1}/exceptions/pending-approval?${params}`, { actionable: [], needs_escalation: [], total: 0 })
      .then((data: any) => {
        setPending(data.actionable ?? []);
        setNeedsEscalation(data.needs_escalation ?? []);
      })
      .catch(() => { setPending([]); setNeedsEscalation([]); });
  };

  const loadConfig = () => {
    return Promise.all([
      safeFetch(`${API_V1}/workflows`, { templates: [] }),
      safeFetch(`${API_V1}/workflows/stats`, null),
    ]).then(([tpl, st]) => {
      const t = tpl?.templates ?? tpl;
      setTemplates(Array.isArray(t) ? t : []);
      setStats(st || null);
    });
  };

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([loadPending(), loadConfig()])
      .then(() => setLoading(false))
      .catch(e => { setError(e.message); setLoading(false); });
  };

  // Re-fetch when role changes (DOA level determines actionable vs needs_escalation split)
  // SSE replaces 15s polling — refresh on approval_update events
  useEffect(() => {
    load();
    const unsub = realtimeService.on('approval_update', () => { loadPending(); });
    return () => { unsub(); };
  }, [role]);

  if (loading) return <div><div style={{ padding: 32, color: 'var(--text-hint)' }}>Loading approval queue...</div></div>;
  if (error && !pending.length && !templates.length) return (
    <div>
      <div style={{ color: 'var(--negative)', marginBottom: 8 }}>Error: {error}</div>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const severityBadge = (sev: string) => {
    const bg = sev === 'CRITICAL' ? 'rgba(239,68,68,0.08)' : sev === 'HIGH' ? 'rgba(245,158,11,0.08)' : 'rgba(30,58,95,0.08)';
    const color = sev === 'CRITICAL' ? 'var(--negative)' : sev === 'HIGH' ? 'var(--warning)' : 'var(--navy-800)';
    return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color }}>{sev}</span>;
  };

  const levelBadge = (amount: number) => {
    const lv = getRequiredLevel(amount);
    const c = LEVEL_COLORS[lv] || '#64748B';
    return <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: c }}>{lv}</span>;
  };

  const pendingColumns: Column[] = [
    { key: 'exception_ref', label: 'Ref', render: (v: string) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{v}</span> },
    { key: 'exception_type', label: 'Type', render: (v: string) => titleCase(v) },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => <span style={{ fontFamily: 'var(--font-mono)' }}>{formatAmount(v ?? 0)}</span> },
    { key: 'department', label: 'Department', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{titleCase(v)}</span> },
    { key: 'severity', label: 'Severity', render: (v: string) => severityBadge(v) },
    { key: 'level', label: 'Level', render: (_: any, row: PendingApproval) => levelBadge(row.amount ?? 0) },
    { key: 'status', label: 'Status', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{titleCase(v)}</span> },
    { key: 'created_at', label: 'Created', render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>{v ? new Date(v).toLocaleDateString() : '-'}</span> },
  ];

  const escalationColumns: Column[] = [
    { key: 'exception_ref', label: 'Ref', render: (v: string) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{v}</span> },
    { key: 'exception_type', label: 'Type', render: (v: string) => titleCase(v) },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => <span style={{ fontFamily: 'var(--font-mono)' }}>{formatAmount(v ?? 0)}</span> },
    { key: 'department', label: 'Department', render: (v: string) => <span style={{ color: 'var(--text-secondary)' }}>{titleCase(v)}</span> },
    { key: 'severity', label: 'Severity', render: (v: string) => severityBadge(v) },
    { key: 'level', label: 'Level', render: (_: any, row: PendingApproval) => levelBadge(row.amount ?? 0) },
    { key: 'reason', label: 'Reason', render: (_: any, row: PendingApproval) => <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>{row.permissions?.approval_blocked_reason ?? 'Exceeds DOA limit'}</span> },
  ];

  const templateColumns: Column[] = [
    { key: 'code', label: 'Code', render: (v: string) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{v}</span> },
    { key: 'name', label: 'Name' },
    { key: 'priority', label: 'Priority', align: 'center', render: (v: number) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{v}</span> },
    { key: 'match_criteria', label: 'Match Criteria', render: (v: Record<string, any>) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{summarizeCriteria(v)}</span> },
    { key: 'steps', label: 'Steps', align: 'center', render: (v: any[]) => (
      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: 'rgba(30,58,95,0.06)', color: 'var(--navy-800)' }}>
        {v?.length || 0}
      </span>
    )},
    { key: 'is_active', label: 'Active', align: 'center', render: (v: boolean) => (
      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: v ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', color: v ? 'var(--positive)' : 'var(--negative)' }}>
        {v ? 'YES' : 'NO'}
      </span>
    )},
  ];

  return (
    <div>
      {(pending.length + needsEscalation.length) > 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 16 }}>
          {pending.length} actionable, {needsEscalation.length} needs escalation
        </div>
      )}
      {error && (
        <div style={{ padding: '8px 16px', marginBottom: 16, background: 'rgba(239,68,68,0.06)', borderRadius: 6, fontSize: 13, color: 'var(--negative)' }}>
          {error}
        </div>
      )}

      <TabBar
        tabs={[
          { key: 'pending', label: 'Pending Approvals', count: pending.length + needsEscalation.length },
          { key: 'config', label: 'Workflow Config' },
        ]}
        active={tab}
        onChange={t => setTab(t as Tab)}
      />

      {/* Tab 1: Pending Approvals */}
      {tab === 'pending' && (
        <>
          {/* Actionable section */}
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--positive)', marginBottom: 8 }}>
              Actionable ({pending.length})
            </h3>
            <DataTable
              columns={pendingColumns}
              rows={pending}
              rowKey="id"
              emptyMessage="No actionable approvals for your role."
            />
          </div>

          {/* Needs Escalation section */}
          {needsEscalation.length > 0 && (
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--warning)', marginBottom: 8 }}>
                Needs Escalation ({needsEscalation.length})
              </h3>
              <DataTable
                columns={escalationColumns}
                rows={needsEscalation}
                rowKey="id"
              />
            </div>
          )}
        </>
      )}

      {/* Tab 2: Workflow Config */}
      {tab === 'config' && (
        <>
          {/* Stats summary */}
          {stats && (
            <>
              <KpiGrid columns={4} items={[
                { label: 'Templates', value: stats.templates_total, color: 'var(--text-primary)' },
                { label: 'Active', value: stats.templates_active, color: 'var(--positive)' },
                { label: 'Pending Instances', value: stats.instances_pending, color: 'var(--warning)' },
                { label: 'SLA Breached', value: stats.sla_breached, color: 'var(--negative)' },
              ]} />
              <KpiGrid columns={3} items={[
                { label: 'Total Instances', value: stats.instances_total, color: 'var(--text-primary)' },
                { label: 'Approved', value: stats.instances_approved, color: 'var(--positive)' },
                { label: 'Rejected', value: stats.instances_rejected, color: 'var(--negative)' },
              ]} />
            </>
          )}

          {/* Templates table */}
          <DataTable
            columns={templateColumns}
            rows={templates}
            rowKey="id"
            emptyMessage="No workflow templates configured."
          />
        </>
      )}
    </div>
  );
}

/** Summarize match_criteria JSON into a short human-readable string */
function summarizeCriteria(criteria: Record<string, any> | null | undefined): string {
  if (!criteria || Object.keys(criteria).length === 0) return 'Any';
  const parts: string[] = [];
  if (criteria.min_amount) parts.push(`>= ${formatAmount(criteria.min_amount)}`);
  if (criteria.max_amount) parts.push(`<= ${formatAmount(criteria.max_amount)}`);
  if (criteria.exception_types) {
    const types = Array.isArray(criteria.exception_types) ? criteria.exception_types : [criteria.exception_types];
    parts.push(types.map(t => titleCase(t)).join(', '));
  }
  if (criteria.departments) {
    const depts = Array.isArray(criteria.departments) ? criteria.departments : [criteria.departments];
    parts.push(depts.map(d => titleCase(d)).join(', '));
  }
  if (criteria.entity_codes) {
    parts.push(`Entities: ${Array.isArray(criteria.entity_codes) ? criteria.entity_codes.join(', ') : criteria.entity_codes}`);
  }
  return parts.length > 0 ? parts.join(' | ') : 'Custom';
}
